import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import {
  deleteLectureMaterialCall,
  getLectureMaterialStatusCall,
} from "../../redux/api/copilot.api";

/*
  ─── Microlab Copilot — Processing screen ────────────────────
  Web equivalent of LectureLabProcessingScreen.tsx. Polls
  GET /lecture-lab/materials/:id/status every 1.5s and steps a
  visual pipeline (extracting → understanding → quizzing → …)
  in lockstep with the server's `pipeline_step` field. When the
  status flips to:
    • ready          → navigate to the Study Pack viewer
    • failed         → auto-delete the material (best effort)
                       so the hub list stays clean, surface a
                       short retry hint, and bounce back to the
                       hub after the user acknowledges.

  Pipeline mapping (server → UI key):
    extracting / transcribing → 'extract'
    identifying_concepts      → 'understand'
    building_summary          → 'summary'
    generating_quizzes        → 'quiz'
    creating_flashcards       → 'flashcards'
    organizing_images         → 'images'
    finalizing                → 'finalize'

  Server can hand us a step we don't know about — we treat any
  unknown step as "the previous step is done, the next is in
  flight" by just bumping the progress bar slightly.
*/

const STEP_ORDER = [
  { key: "extract", label: "Extracting content", hint: "Reading the file" },
  { key: "understand", label: "Identifying concepts", hint: "Mapping topics" },
  { key: "summary", label: "Building summary", hint: "Distilling key ideas" },
  { key: "quiz", label: "Generating quiz", hint: "Drafting MCQs" },
  { key: "flashcards", label: "Creating flashcards", hint: "Front + back pairs" },
  { key: "images", label: "Organising figures", hint: "Captioning images" },
  { key: "finalize", label: "Finalising study pack", hint: "Almost there" },
];

const SERVER_STEP_TO_KEY = {
  extracting: "extract",
  transcribing: "extract",
  identifying_concepts: "understand",
  building_summary: "summary",
  generating_quizzes: "quiz",
  creating_flashcards: "flashcards",
  organizing_images: "images",
  finalizing: "finalize",
};

function activeIndexFromMaterial(material) {
  if (!material) return 0;
  if (material.status === "ready") return STEP_ORDER.length;
  const k = SERVER_STEP_TO_KEY[material.pipeline_step || material.pipelineStep];
  const idx = STEP_ORDER.findIndex((s) => s.key === k);
  return idx === -1 ? 0 : idx;
}

export default function MicrolabCopilotProcessing() {
  const { materialId } = useParams();
  const navigate = useNavigate();
  const [material, setMaterial] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      if (cancelled || !materialId) return;
      const next = await getLectureMaterialStatusCall(materialId);
      if (cancelled) return;
      if (!next) {
        // 404 / network — treat as a transient error but don't
        // unmount; one more tick will retry.
        return;
      }
      setMaterial(next);
      if (next.status === "ready") {
        stopPolling();
        // Tiny pause so the user actually sees the "finished" UI
        // before navigation rather than a hard cut.
        setTimeout(() => {
          if (!cancelled) navigate(`/microlab-copilot/study/${materialId}`, { replace: true });
        }, 450);
      } else if (next.status === "failed") {
        stopPolling();
        setError({
          code: next.failureCode || next.failure_code || "FAILED",
          message:
            next.errorMessage ||
            next.error_message ||
            "We couldn't extract enough content from this file to build a study pack.",
        });
        // Mirror mobile behaviour — soft-delete the failed
        // material so it doesn't linger in the hub list. Best
        // effort; ignore failures.
        deleteLectureMaterialCall(materialId).catch(() => null);
      }
    };

    poll();
    pollRef.current = setInterval(poll, 1500);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [materialId, navigate, stopPolling]);

  const activeIdx = activeIndexFromMaterial(material);
  const percent = Math.min(100, Math.round(((activeIdx + (material?.status === "ready" ? 0 : 0.5)) / STEP_ORDER.length) * 100));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={4} sx={{ maxWidth: 880, mx: "auto", px: { xs: 0.5, md: 1 } }}>
        <Paper
          elevation={0}
          sx={(t) => ({
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            border: "1px solid",
            borderColor: t.palette.divider,
            backgroundImage:
              "linear-gradient(135deg, rgba(59,130,246,0.06) 0%, rgba(168,85,247,0.04) 100%)",
          })}
        >
          {error ? (
            <Stack alignItems="center" textAlign="center" spacing={2} sx={{ py: 4 }}>
              <ErrorOutlineRoundedIcon sx={{ fontSize: 56, color: "error.main" }} />
              <MDTypography variant="h5" fontWeight="bold">
                We couldn't finish your study pack
              </MDTypography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
                {error.message} Your credits have been refunded.
              </Typography>
              <MDButton variant="gradient" color="info" onClick={() => navigate("/microlab-copilot")}>
                Back to Microlab Copilot
              </MDButton>
            </Stack>
          ) : (
            <Stack spacing={3} alignItems="center">
              <Stack alignItems="center" spacing={1.2}>
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress
                    variant="determinate"
                    value={percent}
                    size={120}
                    thickness={4}
                    sx={(t) => ({
                      color: t.palette.primary.main,
                      "& .MuiCircularProgress-circle": {
                        strokeLinecap: "round",
                      },
                    })}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                      {percent}%
                    </Typography>
                  </Box>
                </Box>
                <MDTypography variant="h4" fontWeight="bold" sx={{ letterSpacing: "-0.02em" }}>
                  Turning your file into a study pack
                </MDTypography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 540, textAlign: "center" }}>
                  Microlab Copilot is reading{material?.title ? <> "<strong>{material.title}</strong>"</> : " your file"} and
                  drafting a personalized study pack with a summary, quiz, flashcards and topic chart. This usually takes
                  30–90 seconds.
                </Typography>
              </Stack>

              <Stack
                spacing={1}
                sx={{
                  width: "100%",
                  maxWidth: 560,
                }}
              >
                {STEP_ORDER.map((s, i) => {
                  const done = i < activeIdx || material?.status === "ready";
                  const active = !done && i === activeIdx && material?.status !== "ready";
                  return (
                    <Paper
                      key={s.key}
                      variant="outlined"
                      sx={(t) => ({
                        p: 1.2,
                        borderRadius: 2,
                        borderColor: active
                          ? t.palette.primary.main
                          : done
                            ? alpha(t.palette.success.main, 0.45)
                            : t.palette.divider,
                        backgroundColor: active
                          ? alpha(t.palette.primary.main, 0.06)
                          : "transparent",
                        transition: "all .2s ease",
                      })}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.2}>
                        {done ? (
                          <CheckCircleRoundedIcon sx={{ color: "success.main" }} />
                        ) : active ? (
                          <CircularProgress size={20} />
                        ) : (
                          <RadioButtonUncheckedRoundedIcon sx={{ color: "text.disabled" }} />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography fontWeight={600}>{s.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {s.hint}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>

              <Typography variant="caption" color="text.disabled">
                You can safely close this tab — your study pack will be waiting in the hub once it's ready.
              </Typography>
            </Stack>
          )}
        </Paper>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
